import { T } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Loader2, Phone } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SwissCross } from "@/components/Header";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import { PHONE_PREFIXES, joinPhone } from "@/lib/phone";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [prefix, setPrefix] = useState("+41");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error(T("Password minima 6 caratteri"));
    setLoading(true);
    try {
      await register(email, password, name, joinPhone(prefix, phone));
      toast.success(`Benvenuto in Aurora-ADV, ${name}!`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#09090b] text-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "radial-gradient(ellipse at top right, rgba(230,79,167,0.12), transparent 50%), radial-gradient(ellipse at bottom left, rgba(74,144,226,0.1), transparent 50%)",
      }}/>
      <div className="relative flex-1 flex items-center justify-center p-6 z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img src="/brand/aurora-mark.webp" alt="AURORA-ADV logo · agenzia marketing digitale Ticino" className="w-10 h-10" />
            <div>
              <div className="font-display text-lg leading-none">Aurora<span className="aurora-text">-ADV</span></div>
              <div className="text-[10px] uppercase tracking-widest text-[#71717a] font-mono mt-1 flex items-center gap-1.5">
                <SwissCross className="w-2.5 h-2.5" /> Made in Switzerland
              </div>
            </div>
          </Link>

          <h1 className="font-display text-4xl mb-2">{T("Crea account")}</h1>
          <p className="text-sm text-[#a1a1aa] mb-8">{T("30 secondi, nessuna carta richiesta per iniziare.")}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
              <Input data-testid={TID.nameInput} type="text" placeholder={T("Nome e cognome")}
                value={name} onChange={(e) => setName(e.target.value)}
                className="pl-10 h-12 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] rounded-sm font-mono text-sm"
                required />
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
              <Input data-testid={TID.emailInput} type="email" autoComplete="email" placeholder={T("email@azienda.com")}
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] rounded-sm font-mono text-sm"
                required />
            </div>
            <div data-testid="register-phone-group" className="flex gap-2">
              <select data-testid="register-phone-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)}
                className="h-12 bg-[#18181b] border border-[#27272a] text-white rounded-sm font-mono text-sm px-2">
                {PHONE_PREFIXES.map(([p, c]) => <option key={p} value={p}>{c} {p}</option>)}
              </select>
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
                <Input data-testid="register-phone-input" type="tel" autoComplete="tel-national" placeholder={T("Cellulare es. 79 123 45 67")}
                  value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ""))}
                  className="pl-10 h-12 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] rounded-sm font-mono text-sm"
                  required minLength={6} />
              </div>
            </div>
            <PasswordInput testid={TID.passwordInput} autoComplete="new-password" placeholder={T("min 6 caratteri")}
              value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" data-testid={TID.submitRegisterBtn} disabled={loading}
              className="w-full h-12 aurora-gradient hover:opacity-90 text-white rounded-sm font-medium">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : T("Crea account")}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-[10px] text-[#52525b] uppercase tracking-widest font-mono">
            <div className="flex-1 h-px bg-[#27272a]" />
            {T("oppure")}
            <div className="flex-1 h-px bg-[#27272a]" />
          </div>

          <Button data-testid={TID.googleLoginBtn} onClick={onGoogle} variant="outline"
            className="w-full h-12 bg-transparent border-[#27272a] text-white hover:bg-[#18181b] hover:text-white rounded-sm">
            {T("Continua con Google")}
          </Button>

          <p className="mt-8 text-sm text-[#71717a] text-center">
            {T("Hai già un account?")}{" "}
            <Link data-testid={TID.goToLogin} to="/login" className="aurora-text font-medium">{T("Accedi")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
